import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Brand, BrandManagementColumn, BrandSummary, SortState } from '../types';
import { ClientSideRowModelModule, GridOptions, RowClickedEvent, themeQuartz, type ColDef, SortChangedEvent } from "ag-grid-community";
import { AgGridAngular } from 'ag-grid-angular';
import { AgTooltipCellComponent } from '../../../shared/components/ag-tooltip-cell/ag-tooltip-cell.component';
import { BrandNameLogoRendererComponent } from '../../../shared/components/brand-name-logo/brand-name-logo-renderer.component';
import { getManagementDateFormat } from '../../../utils/time';

@Component({
  selector: 'app-brands-table',
  imports: [AgGridAngular],
  templateUrl: './brands-table.component.html',
  styleUrl: './brands-table.component.scss'
})
export class BrandsTableComponent {
  @Input() rows: Brand[] = [];
  @Output() rowClicked = new EventEmitter<Brand>();
  @Output() sortChanged = new EventEmitter<SortState>();

  colDefs: ColDef[] = this.getColDef();
  theme = themeQuartz.withParams({
    spacing: 10,
    headerBackgroundColor: 'rgba(217, 217, 217, 0.3)',
    rowHoverColor: 'rgba(233, 244, 245, 0.85)',
    fontSize: '14px',
    headerFontSize: '16px',
    fontFamily: 'Poppins',
    headerFontFamily: 'Poppins',
    headerFontWeight: 600,
    headerTextColor: '#717172;',
    cellTextColor: '#717172;',
    headerHeight: 64,
  });
  modules = [ClientSideRowModelModule];
  gridOptions: GridOptions = {
    suppressMovableColumns: true,
    domLayout: 'autoHeight',
    rowHeight: undefined,
    defaultColDef: {
      resizable: false,
      autoHeight: true,
      sortable: true,
      wrapText: true,
      flex: 1
    },
  };

  public onRowClicked = (event: RowClickedEvent): void => {
    this.rowClicked.emit(event.data as Brand);
  };

  public onSortChanged(event: SortChangedEvent): void {
    // ag-grid fires sortChanged for non-user reasons too (initialization, row-data updates,
    // flex/resize). Only a real header click should reload the table.
    if (event.source !== 'uiColumnSorted') {
      return;
    }
    const sortModel = event.columns?.[0];
    this.sortChanged.emit({
      sortBy: sortModel?.getColId() || '',
      sortDir: (sortModel?.getSort() as 'asc' | 'desc') || 'desc',
    });
  }

  private getColDef(): ColDef[] {
    return [
      {
        field: BrandManagementColumn.BRAND_KEY,
        cellRenderer: BrandNameLogoRendererComponent,
        flex: 2, // most important column — give it room so the key isn't cut off
        valueGetter: (params: any) => {
          return {
            name: params.data.brandKey,
            logoPath: params.data.logoPath
          };
        },
      },
      {
        field: BrandManagementColumn.BRAND_NAME,
      },
      {
        field: BrandManagementColumn.ACCOUNTS,
        headerName: "Accounts",
        cellRenderer: AgTooltipCellComponent,
        valueGetter: (params: any) => {
          const domains = params.data?.[BrandManagementColumn.ACCOUNTS];
          return domains.join(', ');
        },
        sortable: false,
      },
      {
        field: BrandManagementColumn.ADVERTISER_DOMAINS,
        cellRenderer: AgTooltipCellComponent,
        valueGetter: (params: any) => {
          const domains: string[] = params.data?.[BrandManagementColumn.ADVERTISER_DOMAINS] ?? [];
          if (domains.length <= 1) {
            return domains[0] ?? '';
          }
          return `${domains[0]} +${domains.length - 1}`;
        },
        sortable: false,
      },
      {
        field: BrandManagementColumn.COMPETITORS,
        headerName: "Competitors",
        cellRenderer: AgTooltipCellComponent,
        valueGetter: (params: any) => {
          return params.data[BrandManagementColumn.COMPETITORS]
            .map((item: BrandSummary) => item?.name)
            .filter(Boolean)
            .join(', ');
        },
        sortable: false,
      },
      {
        field: BrandManagementColumn.CREATED_AT,
        cellRenderer: AgTooltipCellComponent,
        valueGetter: (params: any) => {
          return getManagementDateFormat(params.data[BrandManagementColumn.CREATED_AT])
        }
      }
    ];
  }
}
